import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import { forgotPassword } from '@/api/auth';
import { Button } from '@/components/ui/Button';

export function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await forgotPassword(email.trim());
      setSubmitted(true);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(t('auth:forgotPassword.genericError'));
      } else {
        setError(t('auth:login.errors.unexpected'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">{t('common:appName')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('auth:forgotPassword.title')}</p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          {submitted ? (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-gray-900">{t('auth:forgotPassword.successTitle')}</h2>
              <p className="mt-2 text-sm text-gray-500">
                <Trans
                  i18nKey="auth:forgotPassword.successMessage"
                  values={{ email }}
                  components={{ b: <span className="font-medium text-gray-700" /> }}
                />
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="text-sm text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline"
                >
                  {t('auth:forgotPassword.backToSignIn')}
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <p className="mb-4 text-sm text-gray-600">
                {t('auth:forgotPassword.description')}
              </p>

              {error && (
                <div role="alert" className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth:forgotPassword.emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
                  placeholder={t('auth:login.emailPlaceholder')}
                  disabled={isSubmitting}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={email.trim().length === 0 || isSubmitting}
              >
                {isSubmitting ? t('auth:forgotPassword.submitting') : t('auth:forgotPassword.submit')}
              </Button>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="text-sm text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline"
                >
                  {t('auth:forgotPassword.backToSignIn')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
